import { useEffect, useState, useCallback } from "react";
import { DeviceMobile, CaretRight, ShareNetwork, PlusSquare, X } from "@phosphor-icons/react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());

export default function PwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIosSheet, setShowIosSheet] = useState(false);
  const ios = isIos();

  useEffect(() => {
    if (installed) return;
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [installed]);

  const handleClick = useCallback(async () => {
    if (ios) {
      setShowIosSheet(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
    }
  }, [ios, deferredPrompt]);

  // Already installed → nothing to show
  if (installed) return null;
  // Non-iOS browsers that can't install (no prompt available yet) → hide to avoid dead button
  if (!ios && !deferredPrompt) return null;

  return (
    <>
      <button
        type="button"
        data-testid="pwa-install-button"
        onClick={handleClick}
        className="group flex w-full items-center gap-3 rounded-2xl border border-[#FBCFE8] bg-white/80 px-4 py-3 text-left transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FCE7F3]">
          <DeviceMobile size={20} weight="duotone" className="text-[#DB2777]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-heading text-sm font-extrabold leading-tight text-[#831843]">Pasang HalloGBS di HP</span>
          <span className="mt-0.5 block text-xs leading-snug text-slate-500">Akses cepat langsung dari layar utama, seperti aplikasi.</span>
        </span>
        <CaretRight size={18} weight="bold" className="shrink-0 text-[#DB2777] transition-transform duration-200 group-hover:translate-x-1" />
      </button>

      {showIosSheet && (
        <div
          data-testid="pwa-ios-sheet"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-6 backdrop-blur-sm sm:items-center"
          onClick={() => setShowIosSheet(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-extrabold text-[#831843]">Pasang di iPhone / iPad</h3>
              <button
                type="button"
                data-testid="pwa-ios-close"
                onClick={() => setShowIosSheet(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-50 text-[#DB2777]"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <p className="mb-4 text-sm text-slate-500">Buka melalui Safari, lalu ikuti langkah berikut:</p>
            <ol className="space-y-3">
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FCE7F3] text-sm font-bold text-[#DB2777]">1</span>
                <span className="flex items-center gap-2 text-sm text-slate-700">
                  Ketuk ikon <ShareNetwork size={20} weight="duotone" className="text-[#2563EB]" /> <b>Bagikan</b> di bilah Safari.
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FCE7F3] text-sm font-bold text-[#DB2777]">2</span>
                <span className="flex items-center gap-2 text-sm text-slate-700">
                  Pilih <PlusSquare size={20} weight="duotone" className="text-[#DB2777]" /> <b>Tambahkan ke Layar Utama</b>.
                </span>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FCE7F3] text-sm font-bold text-[#DB2777]">3</span>
                <span className="text-sm text-slate-700">Ketuk <b>Tambah</b>, dan HalloGBS akan muncul di layar utama Anda.</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
